import { PUBLIC_HOME } from "@/lib/native";
import SiteMenu from "@/components/SiteMenu";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Lock, Loader2, Users, ArrowLeft, MessageCircle } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import CelestialBackground from "@/components/CelestialBackground";
import SignSample from "@/components/SignSample";
import AskStarsBanner from "@/components/AskStarsBanner";
import { SignFaq, BestMatches } from "@/components/SignExtras";

const SIGNS = ["aries", "taurus", "gemini", "cancer", "leo", "virgo", "libra", "scorpio", "sagittarius", "capricorn", "aquarius", "pisces"];
const cap = (x) => x.charAt(0).toUpperCase() + x.slice(1);

function AskAboutUs({ sign, from }) {
  const { user } = useAuth();
  const q = `How compatible is my ${cap(sign)} with my ${cap(from)} friend?`;
  const to = `${user ? "/ask" : "/try"}?q=${encodeURIComponent(q)}`;
  return (
    <Link
      to={to}
      data-testid="btn-ask-about-us"
      className="mt-4 flex items-center justify-center gap-2 w-full border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 text-[#E8DCC4] py-3 rounded-full transition-colors"
    >
      <MessageCircle size={15} className="gold-text" /> Ask Astra Interactive about you two
    </Link>
  );
}

function CompatTeaser({ sign, from }) {
  const [c, setC] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get(`/public/compat/${sign}/${from}`).then(({ data }) => setC(data)).catch(() => setFailed(true));
  }, [sign, from]);
  if (failed) return null;
  return (
    <div data-testid="shared-compat" className="bg-[#0F1523] border border-[#D4AF37]/35 rounded-2xl p-5">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] gold-text mb-1">
        <Users size={14} /> You & your {cap(from)} friend <Lock size={12} className="ml-auto text-[#A7B0C0]" />
      </p>
      <p className="text-sm text-[#A7B0C0] mb-3">{cap(sign)} × {cap(from)} compatibility</p>
      {!c ? (
        <div className="flex items-center gap-2 text-sm text-[#A7B0C0]"><Loader2 size={14} className="animate-spin gold-text" /> Comparing your stars…</div>
      ) : (
        <>
          <p data-testid="shared-compat-teaser" className="text-base leading-relaxed text-[#D6D0C5]">{c.teaser}</p>
          <p className="text-base leading-relaxed text-[#D6D0C5] opacity-40 blur-[2px] select-none" aria-hidden>
            Where one leads, the other learns to follow, and the space between you holds a lesson both of you came here to learn.
          </p>
          <p className="text-base leading-relaxed text-[#D6D0C5] opacity-15 blur-[4px] select-none" aria-hidden>
            Your communication styles meet in surprising ways, and the friction points reveal more than they hide.
          </p>
        </>
      )}
      <AskAboutUs sign={sign} from={from} />
    </div>
  );
}

export const PERIODS = {
  daily: { api: "", tab: "Today", label: "Horoscope Today", when: "today", freq: "day" },
  love: { api: "", tab: "Love", label: "Love Horoscope Today", when: "today", freq: "day" },
  money: { api: "", tab: "Money", label: "Money Horoscope Today", when: "today", freq: "day" },
  weekly: { api: "/week", tab: "This week", label: "Weekly Horoscope", when: "this week", freq: "week" },
  monthly: { api: "/month", tab: "This month", label: "Monthly Horoscope", when: "this month", freq: "month" },
};

function PeriodLinks({ sign, period }) {
  return (
    <nav aria-label={`${cap(sign)} horoscope pages`} data-testid="shared-sign-periods" className="grid grid-cols-5 gap-1.5 mb-4">
      {Object.entries(PERIODS).map(([k, p]) => (
        <Link key={k} to={`/s/${sign}/${k}`} data-testid={`shared-sign-period-${k}`} aria-current={period === k ? "page" : undefined}
          className={`whitespace-nowrap text-center text-xs sm:text-sm px-1 py-2 rounded-full border transition-colors ${period === k ? "bg-[#D4AF37] text-[#04060B] border-[#D4AF37]" : "border-[#D4AF37]/30 text-[#E8DCC4] hover:bg-[#D4AF37]/10"}`}>
          <span className="sr-only">{cap(sign)} </span><span className="sm:hidden">{p.tab.replace("This w", "W").replace("This m", "M")}</span><span className="hidden sm:inline">{p.tab}</span>
        </Link>
      ))}
    </nav>
  );
}

// Old /s/aries?week=1 style links and bare /s/aries land on the path-based page.
export function SignRedirect() {
  const { sign } = useParams();
  const [params] = useSearchParams();
  const period = params.get("month") === "1" ? "monthly" : params.get("week") === "1" ? "weekly" : "daily";
  const from = params.get("from");
  return <Navigate replace to={`/s/${(sign || "").toLowerCase()}/${period}${from ? `?from=${encodeURIComponent(from)}` : ""}`} />;
}

function useSignMeta(sign, period) {
  useEffect(() => {
    const p = PERIODS[period];
    const prev = document.title;
    document.title = `${cap(sign)} ${p.label} · Astra`;
    const tag = (sel, make) => document.head.querySelector(sel) || document.head.appendChild(make());
    const canon = tag('link[rel="canonical"]', () => Object.assign(document.createElement("link"), { rel: "canonical" }));
    canon.href = `https://astralogico.com/s/${sign}/${period}`;
    const desc = document.head.querySelector('meta[name="description"]');
    const prevDesc = desc?.content;
    const topic = period === "love" ? "love and relationships" : period === "money" ? "money and career" : "love, career and wellbeing";
    if (desc) desc.content = `Free ${cap(sign)} ${p.label.toLowerCase()} from Astra: ${topic}, written from where the planets really are ${p.when}.`;
    return () => { document.title = prev; canon.remove(); if (desc) desc.content = prevDesc; };
  }, [sign, period]);
}

export default function SharedSign() {
  const { sign: rawSign, period: rawPeriod } = useParams();
  const sign = (rawSign || "").toLowerCase();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const from = (params.get("from") || "").toLowerCase();
  const hasFrom = SIGNS.includes(from);
  const period = PERIODS[rawPeriod] ? rawPeriod : "daily";
  const P = PERIODS[period];
  const [r, setR] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const valid = SIGNS.includes(sign);

  useEffect(() => {
    setR(null);
    setError(false);
    const premiumUrl = { daily: "/signs/today", love: "/signs/today", money: "/signs/today", weekly: "/signs/week", monthly: "/signs/month" }[period];
    api.get(`/public/signs/${sign}${P.api}`).then(async ({ data }) => {
      if (user?.is_premium) {
        const full = await api.get(premiumUrl).then((x) => x.data.signs.find((e) => e.sign.toLowerCase() === sign)).catch(() => null);
        if (full) data = { ...data, love: full.love, money: full.money, overall: full.overall || data.overall };
      }
      setR(data);
    }).catch(() => setError(true));
  }, [sign, P.api, period, attempt, user?.is_premium]);

  useSignMeta(sign, period);
  const focus = period === "love" || period === "money" ? period : null;

  return (
    <div className="app-shell relative text-starlight">
      <CelestialBackground />
      <div className="relative z-10 px-5 pb-16" data-testid="shared-sign-page">
        <header className="safe-top box-content h-16 flex items-center gap-2">
          <SiteMenu />
          <img src="/astra-mark-128.png" alt="" aria-hidden="true" className="w-9 h-9" />
          <span className="font-display text-lg tracking-[0.3em]">ASTRA</span>
          <Link to={user ? "/signs" : PUBLIC_HOME} data-testid="link-shared-all-signs" className="ml-auto flex items-center gap-1 text-sm gold-text">
            <ArrowLeft size={14} /> All 12 signs
          </Link>
        </header>
        {valid && <PeriodLinks sign={sign} period={period} />}
        {error && !valid && <p data-testid="shared-sign-error" className="text-center text-[#A7B0C0] py-20">This star sign couldn't be found.</p>}
        {error && valid && (
          <div data-testid="shared-sign-retry" className="text-center py-20">
            <h1 className="text-3xl font-light mb-2">{cap(sign)} {P.label}</h1>
            <p className="text-[#A7B0C0] mb-5">The stars are taking a moment to load {cap(sign)}'s horoscope.</p>
            <button data-testid="btn-shared-sign-retry" onClick={() => setAttempt((a) => a + 1)} className="border border-[#D4AF37]/50 gold-text px-6 py-2.5 rounded-full hover:bg-[#D4AF37]/10">Try again</button>
          </div>
        )}
        {!r && !error && (
          <div className="flex flex-col items-center py-32 text-[#A7B0C0]">
            <Loader2 className="animate-spin gold-text mb-3" size={28} />
            <p className="italic">Reading the stars…</p>
          </div>
        )}
        {r && (
          <div>
            <SignSample r={r} prefix="shared-sign" onUnlock={() => navigate(user ? "/profile" : "/join")} focus={focus}
              title={focus ? `${cap(sign)} ${P.label}` : undefined}
              afterOverall={<AskStarsBanner to={user ? "/ask" : "/try"} testId="link-ask-stars-shared" showFree={!user?.is_premium} />}>
              {hasFrom && <CompatTeaser sign={sign} from={from} />}
            </SignSample>
            <Link
              to={user ? "/today" : "/join"}
              data-testid="btn-shared-cta"
              className="block text-center w-full mt-6 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors"
            >
              {user ? "Open your personal reading" : "Get your personal reading free"}
            </Link>
            <p className="text-center text-xs text-[#A7B0C0] mt-3">Astra · your daily AI horoscope, written for your birth chart</p>
          </div>
        )}
        {valid && <SignFaq sign={sign} />}
        {valid && <BestMatches sign={sign} />}
      </div>
    </div>
  );
}
